/**
 * Source Digital pixel sink — wraps @source-digital/pixel `Pixel`.
 *
 * First-party-style beacon to pixel.sourcesync.io that survives most ad
 * blockers (not a Google domain). Sends the same events as GA4 but with an
 * arbitrary JSON `ed` data blob, so it can carry far richer context than GA4's
 * 25-param / 500-event limits — this is the raw firehose feeding Redash.
 *
 * `id` is constant (`bkfc`); `platform`/`app` params carry the device axis.
 * Fire-and-forget: `Pixel.send` is wrapped so it never throws into callers.
 */

import { Pixel } from '@source-digital/pixel';
import {
  PIXEL_BEACON_URL,
  PIXEL_ID,
  PIXEL_TRANSPORT,
  PIXEL_OID,
  PLATFORM,
  APP,
  ANALYTICS_VERSION,
  pixelEnabled,
} from './config';

let pixel = null;

/** Construct the Pixel once. Safe to call repeatedly. */
export const initPixel = () => {
  if (pixel || !pixelEnabled()) {
    return;
  }
  try {
    pixel = new Pixel({
      beaconURL: PIXEL_BEACON_URL,
      id: PIXEL_ID,
      transport: PIXEL_TRANSPORT,
      version: ANALYTICS_VERSION,
      trackPageClose: true, // auto "pageclose" event for session duration
      // `oid` ties bkfc-web events to BKFC's SourceSync org (only sent if set).
      params: { platform: PLATFORM, app: APP, ...(PIXEL_OID ? { oid: PIXEL_OID } : {}) },
    });
  } catch (e) {
    pixel = null;
  }
};

/**
 * @param {string} name - event name
 * @param {Object} [data] - event data (serialized into the `ed` blob)
 */
export const pixelEvent = (name, data = {}) => {
  if (!pixel) {
    return;
  }
  try {
    // Stamp the platform axis INTO the `ed` blob (queryable in Redash as
    // JSON_VALUE(ed,'$.platform')) rather than the top-level pixel params,
    // which the GCS ingestion drops. Every event self-identifies its platform
    // so reports can slice by it and an un-instrumented platform reads 0. A
    // caller may override by passing its own `platform`/`app` in `data`.
    // Pixel.send may return a promise (image transport); swallow rejections.
    const result = pixel.send(name, { platform: PLATFORM, app: APP, ...data });
    if (result && typeof result.catch === 'function') {
      result.catch(() => {});
    }
  } catch (e) {
    // swallow — never break the app
  }
};

/** Test/allow-override hook. */
export const __setPixelForTest = p => {
  pixel = p;
};
